import { useExportCsv } from '@/core/hooks/useExportCsv/useExportCsv'
import { Analytics } from '@core/services/mixpanel'
import { getSnakeCaseTodayDate } from '@/ui/routes/issues/components/pdfIssues/utils/utils'
import { generateToast } from '@components/toasting'
import { TECHNOLOGY_TYPE_LABELS } from '@core/modules/digital-footprint/technologies/constants'
import {
    TAssetsExportProps,
    TEmailDomains,
    TSubDomains,
    TTopLevelDomain,
    TIntegrations,
    TCloudApp,
    TDevices,
    TUsers,
} from '../AssetsExport.types'

const COL_WIDTH = 200
const MAX_OPEN_PORTS_ALLOWED = 50

const useExportAssets = ({
    assets,
    exportScope,
    scanInProgress,
    technologies = [],
}: TAssetsExportProps) => {
    const disabled = (!assets.length && !technologies.length) || scanInProgress
    const date = getSnakeCaseTodayDate()
    const fileName = `Stance_${exportScope}_${date}`

    const arrangeTopLevelDomainsData = (
        asset: TAsset
    ): TTopLevelDomain | undefined => {
        if (asset.asset_type === 'domain') {
            return {
                Domain: asset.hostname,
            }
        }
    }

    const arrangeSubdomainsData = (asset: TAsset): TSubDomains | undefined => {
        if (asset.asset_type === 'subdomain') {
            return {
                Host: asset.hostname,
                IP: asset.ip?.toString() || '',
                ISP: asset.isp || '',
                'Open Ports':
                    asset.open_ports && asset.open_ports.length > 0
                        ? JSON.stringify(
                              asset.open_ports.slice(0, MAX_OPEN_PORTS_ALLOWED)
                          )
                        : '',
                Technologies:
                    asset.technologies && asset.technologies.length > 0
                        ? JSON.stringify(asset.technologies)
                        : '',
            }
        }
    }

    const arrangeEmailDomainsData = (
        asset: TAsset
    ): TEmailDomains | undefined => {
        if (asset.asset_type === 'email') {
            return {
                Host: asset.hostname,
                'MX record / IP address': asset.ip?.toString() || '',
                Vendor: asset.mail_vendor || '',
                SPF: asset.spf_error_description,
                DMARC: asset.dmarc_message || '',
            }
        }
    }

    const arrangeIntegrationsData = (
        asset: TAsset
    ): TIntegrations | undefined => {
        if (asset.asset_type === 'integration') {
            return {
                Host: asset.hostname,
                IP: asset.ip?.toString() || '',
                ISP: asset.isp || '',
                Technologies:
                    asset.technologies && asset.technologies.length > 0
                        ? JSON.stringify(asset.technologies)
                        : '',
                'Last Seen': asset.last_seen || '',
            }
        }
    }

    const arrangeCloudAppData = (asset: TAsset): TCloudApp | undefined => {
        if (asset.asset_type === 'cloud_app') {
            return {
                'Provider Name': asset.provider_name || '',
                Host: asset.hostname,
                'Last Seen': asset.last_seen || '',
            }
        }
    }

    const arrangeDevicesData = (asset: TAsset): TDevices | undefined => {
        if (asset.asset_type === 'device') {
            return {
                Host: asset.hostname,
                'Created At': asset.created_at || '',
                'IP Addresses':
                    asset.ip_addresses && asset.ip_addresses.length > 0
                        ? JSON.stringify(asset.ip_addresses)
                        : '',
                Domains:
                    asset.domains && asset.domains.length > 0
                        ? JSON.stringify(asset.domains)
                        : '',
            }
        }
    }

    const arrangeUsersData = (asset: TAsset): TUsers | undefined => {
        if (asset.asset_type === 'user') {
            return {
                Host: asset.hostname,
                'First Name': asset.firstName || '',
                'Last Name': asset.lastName || '',
                Department: asset.department || '',
                Username: asset.username || '',
                Status: asset.status || '',
                'Job Title': asset.jobTitle || '',
                'Mobile Phone': asset.mobilePhone || '',
                'MFA Enabled': asset.mfaEnabled || '',
                'MFA Type': asset.mfaType || '',
                'Last Password Change': asset.lastPasswordChangeAt || '',
                'Password Policy': asset.passwordPolicy || '',
            }
        }
    }

    const prepareData = () => {
        const dataByWs = assets.reduce(
            (acc, curr) => {
                const topLevelDomain = arrangeTopLevelDomainsData(curr)
                const subdomains = arrangeSubdomainsData(curr)
                const emailDomains = arrangeEmailDomainsData(curr)
                const integrations = arrangeIntegrationsData(curr)
                const cloudApps = arrangeCloudAppData(curr)
                const devices = arrangeDevicesData(curr)
                const users = arrangeUsersData(curr)

                topLevelDomain && acc.topLevelDomain.push(topLevelDomain)
                subdomains && acc.subdomains.push(subdomains)
                emailDomains && acc.emailDomains.push(emailDomains)
                integrations && acc.integrations.push(integrations)
                cloudApps && acc.cloudApps.push(cloudApps)
                devices && acc.devices.push(devices)
                users && acc.users.push(users)
                return acc
            },
            {
                topLevelDomain: [] as TTopLevelDomain[],
                subdomains: [] as TSubDomains[],
                emailDomains: [] as TEmailDomains[],
                integrations: [] as TIntegrations[],
                cloudApps: [] as TCloudApp[],
                devices: [] as TDevices[],
                users: [] as TUsers[],
            }
        )

        const technologiesSheet = technologies.map((tech) => ({
            Name: tech.name,
            Type: TECHNOLOGY_TYPE_LABELS[tech.type],
            Version: tech.version ?? '',
            Description: tech.description ?? '',
            'Last Seen': tech.last_seen ?? '',
        }))

        return [
            {
                csvData: dataByWs.topLevelDomain,
                wsName: 'Root Domains',
                colWidth: COL_WIDTH,
            },
            {
                csvData: dataByWs.subdomains,
                wsName: 'Subdomains',
                colWidth: COL_WIDTH,
            },
            {
                csvData: dataByWs.emailDomains,
                wsName: 'Email Domains',
                colWidth: COL_WIDTH,
            },
            {
                csvData: dataByWs.integrations,
                wsName: 'Integrations',
                colWidth: COL_WIDTH,
            },
            {
                csvData: dataByWs.cloudApps,
                wsName: 'Cloud Apps',
                colWidth: COL_WIDTH,
            },
            {
                csvData: dataByWs.devices,
                wsName: 'Devices',
                colWidth: COL_WIDTH,
            },
            { csvData: dataByWs.users, wsName: 'Users', colWidth: COL_WIDTH },
            {
                csvData: technologiesSheet,
                wsName: 'Technologies',
                colWidth: COL_WIDTH,
            },
        ].filter((sheet) => sheet.csvData.length > 0)
    }

    const exportSheets = prepareData()

    const { exportFile, error } = useExportCsv({
        data: exportSheets,
        fileName,
    })

    const handleOnExportClick = () => {
        if (!disabled) {
            Analytics.event('SEM::user_export_assets', {
                scope: exportScope,
                assetTypes: exportSheets.map((sheet) => sheet.wsName),
            })
            exportFile()
        }
    }

    if (error) {
        generateToast({
            type: 'error',
            data: `Export assets to XLSX has failed`,
        })
    }

    return {
        disabled,
        handleOnExportClick,
    }
}

export default useExportAssets
